import {Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {v4 as uuid} from 'uuid';
import {positionDropdownInParent} from "@shared/utils";

// FormulaVariableDefinition, FormulaOperator, etc. - same as before
export class FormulaVariableDefinition {
  code!: string;
  info!: string;
}

export type FormulaOperatorType = 'variable' | 'operator' | 'blank' | 'variable_sis' | 'number';
export type FormulaVariableType = 'ESCALAR' | 'VECTORIAL' | 'FORMULA' | 'SISTEMA';


export interface NewUserVariableEvent {
  name: string,
  type?: FormulaVariableType,
  action: 'update' | 'create',
  success: (name: string, previousName: string, type: FormulaVariableType) => void,
  cancel?: () => void
}

export class FormulaOperator {
  id: string;
  name: FormulaOperatorType;

  constructor(name: FormulaOperatorType) {
    this.name = name;
    this.id = uuid();
  }
}

export class FormulaVariable extends FormulaOperator {
  var: string;
  tipo: FormulaVariableType;

  constructor(type: FormulaOperatorType, variable: string, tipo: FormulaVariableType) {
    super(type);
    this.var = variable;
    this.tipo = tipo;
  }
}

export class FormulaOp extends FormulaOperator {
  text: string;

  constructor(text: string) {
    super('operator');
    this.text = text;
  }
}

export class FormulaNumber extends FormulaOperator {
  text: number;

  constructor(text: string) {
    super('number');
    this.text = Number(text);
  }
}

@Component({
  selector: 'az-formula',
  templateUrl: './formula.component.html',
  styleUrls: ['./formula.component.scss']
})
export class FormulaComponent implements OnInit, OnChanges {
  @Input() formula: FormulaOperator[] = [];
  @Input() editable: boolean = true;
  @Input() operators: FormulaOp[] = [];
  @Input() systemVariables: FormulaVariableDefinition[] = [];
  @Input() usrVariables: FormulaVariable[] = [];
  @Output() formulaChange = new EventEmitter();
  @Output() createUserVariable = new EventEmitter<NewUserVariableEvent>();

  @ViewChild('dropdownSuggestions') dropdownSuggestions: ElementRef<HTMLDivElement>;
  @ViewChild('formularBuilderForm') formularBuilderForm: ElementRef<HTMLDivElement>;

  cursorIndex: number = 0;  // Índice actual en la lista formula
  _dropdownSuggestions: {var: any; text: string; type: 'variable' | 'create_variable' | string, addFn: () => void}[] = [];
  _dropdownSelectedIndex: number = 0;


  constructor() {
  }

  ngOnInit(): void {
    this.assignUuids(this.formula);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.formula?.currentValue) {
      this.assignUuids(this.formula);
      this.cursorIndex = this.formula.length;
    }
  }

  // Formula logic methods
  _spanBuffer: string;

  addNumber(text: string): void {
    const number = new FormulaNumber(text);
    this.addFormulaItem(number);
  }

  addBlank(): void {
    const blank = new FormulaOperator('blank');
    this.addFormulaItem(blank);
  }

  addVariable(name: string, type: FormulaVariableType = 'ESCALAR'): void {
    const variable = new FormulaVariable('variable', name, type);
    this.addFormulaItem(variable);
  }

  updateVariable(previousName: string, name: string) {
    const variables = this.formula.filter((value: FormulaVariable) => value.name == 'variable' && value.var == previousName) as FormulaVariable[];
    variables.forEach(variable => {variable.var = name})
  }

  removeVariable(name: string): void {
    const variable = this.formula.find((value: FormulaVariable) => value.name == 'variable' && value.var == name);
    this.removeFormulaItem(variable.id);
  }

  addSysVariable(name: string, type: FormulaVariableType = 'SISTEMA'): void {
    const variable = new FormulaVariable('variable_sis', name, type);
    this.addFormulaItem(variable);
  }

  addOperator(text: string): void {
    const operator = new FormulaOp(text);
    this.addFormulaItem(operator);
  }

  addFormulaItem(item) {
    this.formula.splice(this.cursorIndex, 0, item);
    this.cursorIndex++;
    this.formulaChange.emit(this.formula);
    setTimeout(() => {
      document.getElementById('formula-item-' + this.cursorIndex)?.focus();
    }, 50);
  }

  removeFormulaItem(id: string, direction: 'backward' | 'forward' = 'backward'): void {
    const index = this.formula.findIndex(item => item.id === id);
    if (index > -1) {
      this.formula.splice(index, 1);
      switch (direction) {
        case 'backward':
          this.cursorIndex = this.cursorIndex > 0 ? this.cursorIndex - 1 : 0;
          break;
      }
      this.focusSpanAtIndex(this.cursorIndex);
      this.formulaChange.emit(this.formula);
    }
  }

  getItemClass(item: FormulaOperator): string {
    return `az-formula-builder-formula-item-${item.name}`;
  }

  getItemText(item: FormulaOperator): string {
    switch (item.name) {
      case 'variable':
      case 'variable_sis':
        return (item as FormulaVariable).var;
      case 'operator':
        return (item as FormulaOp).text;
      case 'number':
        return (item as FormulaNumber).text.toString();
      case 'blank':
        return ' ';
      default:
        return item.name;
    }
  }

  private assignUuids(formula: FormulaOperator[]): void {
    if(!formula) return;
    formula.forEach(item => {
      item.id = uuid();
    });
  }

  closeSuggestionsDropdown() {
    this._dropdownSuggestions = [];
    this._dropdownSelectedIndex = -1;

    setTimeout(() => {
      const dropdowns = document.querySelectorAll('div[id^="dropdown-suggestions-"]');
      dropdowns.forEach((dropdown) => {
        dropdown.classList.add('hidden');
      });
    }, 10);
  }

  generateSuggestionsDropdown(searchTerm: string, element: HTMLSpanElement): void {
    this._spanBuffer = searchTerm;
    if (searchTerm.length < 2) {
      this.closeSuggestionsDropdown();
      return;
    }
    const sysVariables = this.systemVariables.filter(v => v.code.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1);
    const usrVariables = this.usrVariables.filter(v => v.var.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1);

    this._dropdownSuggestions = [...sysVariables.map(v => ({
      var: v,
      text: v.code,
      type: 'sys_variable',
      addFn: () => this.addSysVariable(v.code)
    })), ...usrVariables.map(v => ({var: v, text: v.var, type: 'usr_variable', addFn: () => this.addVariable(v.var)}))];
    this._dropdownSelectedIndex = 0;

    if(isNaN(Number.parseFloat(searchTerm))) {
      this._dropdownSuggestions.push({
        var: searchTerm,
        text: searchTerm,
        type: 'create_variable',
        addFn: () => this.openNewVariableDropdown(searchTerm)
      })
    }

    setTimeout(() => {
      const activeDropdown = document.getElementById('dropdown-suggestions-' + this.cursorIndex);
      if(!activeDropdown) return;
      activeDropdown.classList.remove('hidden');
      const position = positionDropdownInParent(element, activeDropdown, this.formularBuilderForm.nativeElement);
      activeDropdown.classList.add(position == 'left' ? 'left-0' : 'right-0');
      activeDropdown.classList.remove(position == 'left' ? 'right-0' : 'left-0');
    }, 10);
  }


  dropdownFocusPreviousItem() {
    if (this._dropdownSelectedIndex == 0) return;
    this._dropdownSelectedIndex--;
  }

  dropdownFocusNextItem() {
    if (this._dropdownSelectedIndex == this._dropdownSuggestions.length - 1) return;
    this._dropdownSelectedIndex++;
  }


  dropdownInsertClickedItem(i: number) {
    this.clearFocusedSpan();
    this._dropdownSuggestions[i].addFn();
    this._dropdownSuggestions = [];
  }

  dropdownInsertFocusedItem() {
    this.clearFocusedSpan();
    this._dropdownSuggestions[this._dropdownSelectedIndex].addFn();
    this._dropdownSuggestions = [];
  }

  addFormulaItemIfContentMatches(term: string) {
    if (this.systemVariables.map(v => v.code).includes(term)) {
      const variable = this.systemVariables.find(v => v.code === term);
      this.addSysVariable(variable.code);
      this.clearFocusedSpan();
      return;
    }
    if (this.usrVariables.map(v => v.var).includes(term)) {
      const variable = this.usrVariables.find(v => v.var === term);
      this.addVariable(variable.name, variable.tipo);
      this.clearFocusedSpan();
      return;
    }
    if (!isNaN(Number.parseFloat(term))) {
      this.addNumber(term);
      this.clearFocusedSpan();
      return;
    }
  }

  keyOnFormulaEditor(event: KeyboardEvent, item: FormulaOperator, element: HTMLSpanElement) {
    const operatorKeys = this.operators.map(o => o.text);
    const textContent = element.textContent;

    if (operatorKeys.includes(event.key)) {
      this.addFormulaItemIfContentMatches(textContent);
      event.preventDefault();  // Evita que el carácter se inserte en el div
      this.addOperator(event.key);
      return;
    }

    const indexSelection = this.getCursorPositionInElement();
    switch (event.key) {// Si es una flecha derecha o izquierda, manejamos el movimiento del cursor
      case 'Backspace':
        if (textContent.length > 1) {
          this.generateSuggestionsDropdown(textContent.substring(0, textContent.length - 1), element);
          break;
        }
        if (textContent.length == 1) break;
        let itemToDelete = this.formula[this.cursorIndex - 1];
        if (!itemToDelete) break;
        this.removeFormulaItem(itemToDelete.id);
        this.closeSuggestionsDropdown();
        break;
      case 'Delete':
        if (this.cursorIndex == this.formula.length) break;
        if (textContent.length > 0) break;
        const itemToSupr = this.formula[this.cursorIndex];
        this.removeFormulaItem(itemToSupr.id, 'forward');
        break;
      case 'Escape':
        event.preventDefault();
        if(this._dropdownSuggestions.length) this.closeSuggestionsDropdown();
        break;
      case ' ':
        event.preventDefault();
        this.addFormulaItemIfContentMatches(textContent + event.key);
        this.addBlank();
        break;
      case 'End':
        this.changeCursorIndex(this.formula.length);
        break;
      case 'Start':
        this.changeCursorIndex(0);
        break;
      case 'Enter':
        event.preventDefault();
        event.stopPropagation();
        if (this._dropdownSuggestions.length > 0) {
          this.dropdownInsertFocusedItem();
        } else {
          this.addFormulaItemIfContentMatches(textContent);
        }
        break;
      case 'ArrowLeft':
        if (indexSelection > 0) break;
        if (this.cursorIndex == 0) return;
        this.changeCursorIndex(this.cursorIndex - 1);
        this.focusSpanAtIndex(this.cursorIndex);
        break;
      case 'ArrowRight':
        if (indexSelection < textContent.length) break;
        if (this.cursorIndex == this.formula.length) return;
        this.changeCursorIndex(this.cursorIndex + 1);
        this.focusSpanAtIndex(this.cursorIndex);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.dropdownFocusNextItem();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.dropdownFocusPreviousItem();
        break;
      case 'Shift':
      case 'Control':
      case 'Meta':
      case 'Alt':
        break;
      case 'Tab':
        event.preventDefault();
        break;
      default:
        this.generateSuggestionsDropdown(textContent + event.key, element);
        break;
    }
  }

  clearFocusedSpan() {
    setTimeout(() => {
      const focusedSpan = document.getElementById('formula-item-' + this.cursorIndex);
      if (focusedSpan) {
        focusedSpan.textContent = '';
      }
    }, 10);
  }

  focusSpanAtIndex(i: number) {
    this.closeSuggestionsDropdown();
    setTimeout(() => {
      const span = document.getElementById('formula-item-' + i);
      if (span) {
        span.focus();
      }
    }, 10);
  }

  changeCursorIndex(i: number) {
    this.cursorIndex = i;
  }

  getCursorPositionInElement(): number {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0); // Obtiene el rango donde está el cursor

      return range.startOffset;
    }
    return -1;
  }

  openNewVariableDropdown(term: string) {
    this.closeSuggestionsDropdown();
    this.clearFocusedSpan();
    this.createUserVariable.emit({name: term, type: 'ESCALAR', action: 'create',
      success: (name: string, previousName, type: FormulaVariableType) => {
        this.addVariable(name,type);
      }, cancel: () => {}})
  }
}
